import { useState, useMemo } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown, FlaskConical, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useDataStale } from "@/contexts/DataStaleContext";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface LabResult {
  type: "lab_result";
  name?: string | null; // Nome do documento/laudo (apenas para is_main_record)
  is_main_record?: boolean;
  test_name: string | null;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  date: string;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileSessionDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableLabResult extends LabResult {
  _index?: number; // Índice no array para identificar o item
}

export function SmartfileSessionDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileSessionDetailsProps) {
  const isMobile = useIsMobile();
  const { markBothStale } = useDataStale();

  // Buscar o registro principal (is_main_record: true) - memoizado
  const mainRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'lab_result' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
    ) as LabResult | undefined;
  }, [session.structured_records]);

  // Filtrar apenas lab_results (excluir registro principal) - memoizado
  const labResults: EditableLabResult[] = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is LabResult =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'lab_result' &&
        !(record as any).is_main_record // Excluir registro principal
      )
      .map((record: LabResult, index: number) => ({
        ...record,
        _index: index
      }));
  }, [session.structured_records]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditableLabResult[]>(labResults);
  const [editedMainRecord, setEditedMainRecord] = useState<LabResult | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('desc');
  // Map para rastrear estado manual de cada grupo (true = expandido, false = colapsado, undefined = usa padrão)
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedRecords quando labResults mudar
  React.useEffect(() => {
    setEditedRecords(labResults);
    setEditedMainRecord(mainRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let labResultCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'lab_result' &&
          !(record as any).is_main_record) {
          if (labResultCount === index) {
            realIndex = i;
            break;
          }
          labResultCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let labResultCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'lab_result' &&
          !(record as any).is_main_record) {
          if (labResultCount === index) {
            realIndex = i;
            break;
          }
          labResultCount++;
        }
      }

      if (realIndex !== -1) {
        // Remover o registro do array
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        // Marcar visão 360 e análise global como desatualizados
        if (session.patient_id) {
          await markBothStale(session.patient_id);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancel = () => {
    setEditedRecords(labResults);
    setEditingIndex(null);
  };

  const handleSaveMainRecord = async () => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice do registro principal
      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'lab_result' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex !== -1 && editedMainRecord) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelMainRecord = () => {
    setEditedMainRecord(mainRecord);
    setEditingMainRecord(false);
  };

  if (labResults.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum resultado laboratorial encontrado nesta sessão</p>
      </div>
    );
  }

  // Agrupar por mês/ano
  const groupedByDate = labResults.reduce((acc, record, index) => {
    const date = record.date || session.created_at;
    const dateObj = new Date(date);
    // Criar chave no formato "YYYY-MM" para ordenação correta
    const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    // Chave de exibição no formato "MM/YYYY"
    const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    if (!acc[monthYearKey]) {
      acc[monthYearKey] = {
        displayKey,
        records: []
      };
    }
    acc[monthYearKey].records.push({ ...record, _index: index });
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditableLabResult[] }>);

  // Ordenar por mês/ano conforme o estado de ordenação
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime(); // Adicionar dia 01 para comparação
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Toggle de expansão de grupos de data
  const toggleDateGroup = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroups((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  // Toggle de ordenação de data
  const toggleDateSort = () => {
    setDateSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  // Função para renderizar uma linha de registro
  const renderRecordRow = (record: EditableLabResult, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={6} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedRecords[originalIndex]?.value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          value: e.target.value ? parseFloat(e.target.value) : 0
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value || null
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="mg/dl, ng/ml..."
                      className="h-8"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Ref. Mín</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editedRecords[originalIndex]?.ref_low ?? ''}
                        onChange={(e) => {
                          const updated = [...editedRecords];
                          updated[originalIndex] = {
                            ...updated[originalIndex],
                            ref_low: e.target.value ? parseFloat(e.target.value) : null
                          };
                          setEditedRecords(updated);
                        }}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ref. Máx</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editedRecords[originalIndex]?.ref_high ?? ''}
                        onChange={(e) => {
                          const updated = [...editedRecords];
                          updated[originalIndex] = {
                            ...updated[originalIndex],
                            ref_high: e.target.value ? parseFloat(e.target.value) : null
                          };
                          setEditedRecords(updated);
                        }}
                        className="h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{format(new Date(record.date), "dd/MM/yyyy")}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate" title={record.test_name}>{record.test_name}</div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm font-semibold truncate">{record.value}</div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm text-muted-foreground truncate">{record.unit || '–'}</div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-xs text-muted-foreground truncate">
                {record.ref_low !== null || record.ref_high !== null
                  ? `${record.ref_low ?? '–'} - ${record.ref_high ?? '–'}`
                  : '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este resultado?
                        <br />
                        <span className="font-medium mt-2 block">
                          {record.test_name} - {record.value} {record.unit || ''} ({format(new Date(record.date), "dd/MM/yyyy")})
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar um card mobile
  const renderMobileCard = (record: EditableLabResult, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    return (
      <Card key={`${dateKey}-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = {
                      ...updated[originalIndex],
                      date: e.target.value
                    };
                    setEditedRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedRecords[originalIndex]?.value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        value: e.target.value ? parseFloat(e.target.value) : 0
                      };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unidade</Label>
                  <Input
                    value={editedRecords[originalIndex]?.unit || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        unit: e.target.value || null
                      };
                      setEditedRecords(updated);
                    }}
                    placeholder="mg/dl, ng/ml..."
                    className="h-9"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ref. Mín</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedRecords[originalIndex]?.ref_low ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        ref_low: e.target.value ? parseFloat(e.target.value) : null
                      };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ref. Máx</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedRecords[originalIndex]?.ref_high ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        ref_high: e.target.value ? parseFloat(e.target.value) : null
                      };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving} className="flex-1">
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cabeçalho: Título, Data e Botões de Ação */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FlaskConical className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-semibold text-sm leading-tight">
                      {record.test_name || 'Exame não especificado'}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(record.date), "dd/MM/yyyy")}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(originalIndex)}
                    className="h-8 px-2 text-xs"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Excluir
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir este resultado?
                          <br />
                          <span className="font-medium mt-2 block">
                            {record.test_name} - {record.value} {record.unit || ''} ({format(new Date(record.date), "dd/MM/yyyy")})
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(originalIndex)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Três colunas: Valor, Unidade, Referência */}
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-border">
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Valor</span>
                  <span className="text-sm font-medium">{record.value}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Unidade</span>
                  <span className="text-sm font-medium">{record.unit || '–'}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Referência</span>
                  <span className="text-sm font-medium">
                    {record.ref_low !== null || record.ref_high !== null
                      ? `${record.ref_low ?? '–'} - ${record.ref_high ?? '–'}`
                      : '–'}
                  </span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="bg-background border-t">
      {/* Seção: Título Editável (sem cabeçalho "Informações do Documento") */}
      {mainRecord && (
        <div className="mb-4 border-b border-border pb-4 px-4 pt-4">
          {editingMainRecord ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nome do Documento</Label>
                <Input
                  value={editedMainRecord?.name || ''}
                  onChange={(e) => {
                    setEditedMainRecord({
                      ...editedMainRecord!,
                      name: e.target.value || null
                    });
                  }}
                  placeholder="Ex: Exames Laboratoriais - 28/11/2025"
                  className="h-8"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveMainRecord} disabled={isSaving}>
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelMainRecord} disabled={isSaving}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs text-muted-foreground">Nome do Documento</Label>
                <p className="text-sm font-medium mt-1">
                  {mainRecord.name || 'Sem nome'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingMainRecord(true)}
                className="h-7 px-2"
              >
                <Pencil className="h-3 w-3 mr-1" />
                Editar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Mobile: Cards View */}
      {isMobile ? (
        <div className="p-3">
          {sortedDates.map((monthYearKey) => {
            const dateGroup = groupedByDate[monthYearKey];
            const dateRecords = dateGroup.records;
            const displayKey = dateGroup.displayKey;
            const defaultExpanded = expandAllByDefault;
            const manualState = expandedDateGroups.get(monthYearKey);
            const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

            return (
              <div key={monthYearKey} className="mb-4">
                <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center gap-2 text-xs font-semibold text-primary cursor-pointer bg-primary/5 hover:bg-primary/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <span>📅 {displayKey}</span>
                      <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                      <Badge variant="secondary">
                        {dateRecords.length} resultado{dateRecords.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {dateRecords.map((record, groupIndex) => renderMobileCard(record, groupIndex, monthYearKey))}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop: Table View */
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-[120px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleDateSort}
                    className="h-auto p-0 font-semibold hover:bg-transparent gap-1"
                  >
                    Data
                    {dateSortOrder === 'asc' ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    )}
                  </Button>
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-[200px]">
                  Exame
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-[100px]">
                  Valor
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-[100px]">
                  Unidade
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-[150px]">
                  Referência
                </th>
                <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide w-[120px]">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDates.map((monthYearKey) => {
                const dateGroup = groupedByDate[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroups.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <React.Fragment key={monthYearKey}>
                    <tr className="bg-primary/5 border-y-2 border-primary/20">
                      <td colSpan={6} className="p-2">
                        <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                          <CollapsibleTrigger asChild>
                            <div className="group flex items-center gap-2 text-xs font-semibold text-primary cursor-pointer hover:bg-primary/10 rounded px-2 py-1 transition-colors w-full">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                              <span>📅 {displayKey}</span>
                              <div className="flex items-center gap-3 ml-auto">
                                <span className="font-normal text-muted-foreground group-hover:text-muted-foreground">
                                  {isExpanded ? '- Ver menos' : '+ Ver mais'}
                                </span>
                                <Badge variant="secondary">
                                  {dateRecords.length} resultado{dateRecords.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                        </Collapsible>
                      </td>
                    </tr>

                    {isExpanded && (
                      <>
                        {dateRecords.map((record, groupIndex) => {
                          return renderRecordRow(record, groupIndex, monthYearKey);
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {labResults.length} resultado(s) laboratorial(is) em {sortedDates.length} mês(es)/ano(s)
      </div>
    </div>
  );
}

